import { ControlledInput } from '@/components/forms/controlled-input';
import { Logo } from '@/components/logo';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import api from '@/services/api';
import { Ionicons } from '@expo/vector-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, Pressable, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { KEYBOARD_BAR_HEIGHT } from '@/components/keyboard/keyboard-bar';
import { z } from 'zod';
import { getApiErrorMessage } from '@/utils/api-error';

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'Senha deve ter no mínimo 8 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordScreen() {
  const { token } = useLocalSearchParams<{ token?: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { control, handleSubmit } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Sem token na URL o link é inválido: não há o que redefinir.
  useEffect(() => {
    if (!token) {
      setError('Link inválido ou expirado. Peça um novo email de redefinição.');
    }
  }, [token]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;
    try {
      setIsLoading(true);
      setError(null);
      await api.post('/auth/reset-password', {
        token,
        newPassword: data.password,
      });
      setSuccess(true);
      setTimeout(() => router.replace('/(auth)/login'), 2000);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Não foi possível alterar a senha. Tente novamente.'));
    } finally {
      setIsLoading(false);
    }
  };

  const overflowClass =
    Platform.OS === 'web' ? 'overflow-visible min-h-[100dvh]' : 'overflow-hidden';
  return (
    <View className={` flex-1 bg-background ${overflowClass}`}>
      <SeoHead title="Nova senha · Controlapag" noindex />
      <KeyboardAvoidingView
        behavior="padding"
        keyboardVerticalOffset={KEYBOARD_BAR_HEIGHT}
        className={`flex-1 ${overflowClass}`}
      >
        <View className="flex-1 md:mx-auto md:flex md:max-w-md md:flex-col md:justify-center md:p-8">
          <View
            className={` min-h-full w-full bg-card md:flex md:min-h-[400px] md:flex-col md:rounded-3xl md:shadow-2xl ${Platform.OS === 'web' ? 'overflow-visible' : 'overflow-hidden'}`}
          >
            <View className="flex-1 px-6 pb-6 md:flex md:flex-col md:justify-between md:pb-6 md:pt-8">
              <View>
                <View className="mb-6 items-center">
                  <Logo size={64} color="tint" />
                  <ThemedText className="mt-2 font-bold text-xl md:text-2xl">Nova senha</ThemedText>
                  <ThemedText className="mt-2 text-center text-sm text-gray-500">
                    Defina uma nova senha para sua conta
                  </ThemedText>
                </View>

                {success ? (
                  <View className="flex-row items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-4">
                    <Ionicons className="text-success-600" name="checkmark-circle" size={24} />
                    <ThemedText className="flex-1 text-sm text-green-700">
                      Senha alterada com sucesso. Redirecionando para o login...
                    </ThemedText>
                  </View>
                ) : (
                  <View className="gap-3">
                    <ControlledInput
                      control={control}
                      name="password"
                      label="Nova senha"
                      placeholder="••••••••"
                      secureTextEntry
                      autoComplete="new-password"
                    />
                    <ControlledInput
                      control={control}
                      name="confirmPassword"
                      label="Confirmar senha"
                      placeholder="••••••••"
                      secureTextEntry
                      autoComplete="new-password"
                    />
                    {error && (
                      <View className="flex-row items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3">
                        <Ionicons className="text-error-600" name="alert-circle" size={18} />
                        <ThemedText className="flex-1 text-sm text-error-600">{error}</ThemedText>
                      </View>
                    )}
                  </View>
                )}
              </View>

              {!success && (
                <View className="mt-auto px-0 pt-4">
                  <Button
                    title={isLoading ? 'Salvando...' : 'Alterar senha'}
                    onPress={handleSubmit(onSubmit)}
                    disabled={isLoading || !token}
                    size="md"
                    className="mb-4 w-full"
                  />
                  <Pressable onPress={() => router.replace('/(auth)/login')} className="py-2">
                    <ThemedText className="text-center text-sm text-primary">
                      Voltar para o login
                    </ThemedText>
                  </Pressable>
                </View>
              )}
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}
